import { findByUuid } from '@witmetrics/utils/arrays';
import { isNullish } from '@witmetrics/utils/misc';
import {
  camelToTitle,
  containsString,
  isBlank,
  isString,
  toTitleCase,
} from '@witmetrics/utils/strings';
import type { FloatFieldValue } from '@witmetrics/unison-core';
import type { PendingSubmission } from '@/providers/NewCaseProvider';

export const classes = {
  wrapper: 'flex gap-6 size-full overflow-hidden py-6 px-4',
  column: 'w-1/2 h-full flex flex-col gap-4 min-h-0 overflow-y-auto',
  settingsWrapper: 'flex flex-col flex-1 min-h-0',
  settingsHeader: 'place-between gap-6 min-h-10',
  settingsTitle: 'shrink-0 whitespace-nowrap',
  emptySettings: 'text-sm text-grey-6 dark:text-mauve-3 text-center py-4',
  list: 'flex-1 min-h-0 mt-2 overflow-auto border border-black/25 rounded-2xl p-2 outline-none',
  listItem:
    'px-4 py-2 mb-1 rounded-xl hover:bg-grey-2 dark:hover:bg-white/[0.06] transition-colors',
  patientName:
    'px-4 py-2 mb-1 rounded-xl bg-grey-2 dark:bg-white/[0.06] hover:bg-grey-3 dark:hover:bg-white/[0.1] transition-colors place-between cursor-pointer',
  patientLabel: 'text-xs font-normal text-grey-8 dark:text-mauve-1',
  notes: 'w-full my-2',
  detailsField: 'px-0',
  label: 'font-semibold text-grey-8 dark:text-mauve-1 text-sm',
  value: 'text-sm font-normal text-grey-7 dark:text-mauve-2',
  editIcon: 'edit-icon text-grey-8 dark:text-mauve-1',
  addIcon: 'text-grey-8 dark:text-mauve-1',
  noFiles: 'text-base text-grey-6 dark:text-mauve-3 my-2',
  docButton:
    'mt-2 w-full bg-grey-2 dark:bg-white/[0.06] hover:bg-grey-3 dark:hover:bg-white/[0.1] text-base text-grey-8 dark:text-mauve-1 rounded-lg px-3 py-4 place-start gap-2 cursor-pointer',
};

export const styles = {
  detailsField: {
    '&:hover .edit-icon, &:focus-within .edit-icon': {
      opacity: 1,
    },
    '& .edit-icon': {
      opacity: 0,
      transition: 'opacity 0.2s',
    },
    '& .MuiFilledInput-root': {
      borderRadius: '0.75rem',
    },
    '& .MuiFilledInput-root:before, & .MuiFilledInput-root:after': {
      display: 'none !important',
    },
  },
};

export function getSubmission(
  pendingSubmissions: PendingSubmission[],
  uuid?: string
) {
  return uuid ? findByUuid(pendingSubmissions, uuid) : undefined;
}

export function formatValueLabel(value: string): string {
  return toTitleCase(value.replace(/_/g, ' '));
}

export function filterParamKeys(
  params: Record<string, unknown>,
  query: string
): string[] {
  const keys = Object.keys(params);
  if (isBlank(query)) return keys;
  return keys.filter((key) =>
    getSearchableStrings(camelToTitle(key), params[key]).some((s) =>
      containsString(s, query)
    )
  );
}

export function getSearchableStrings(label: string, value: unknown): string[] {
  const result = [label];
  if (isNullish(value)) return result;
  if (Array.isArray(value)) {
    value.forEach((v) => {
      if (isString(v)) result.push(v, formatValueLabel(v));
      else if (!isNullish(v)) result.push(String(v));
    });
  } else if (typeof value === 'object') {
    const fv = value as Partial<FloatFieldValue>;
    if (isString(fv.label)) result.push(fv.label);
    if (!isNullish(fv.value)) result.push(String(fv.value));
  } else if (isString(value)) {
    result.push(value, formatValueLabel(value));
  } else {
    result.push(String(value));
  }
  return result;
}
